import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get } from '../../../api/client';
import type { GrantListResponse, GrantRow } from '../types';

/**
 * 筛选器的候选。显示名以服务端每行下发的 sourceLabel 为准（那里是事实源），
 * 这份列表只负责给出下拉项；后端新增来源时这里少一项，不影响记录本身读得懂。
 */
const SOURCE_OPTIONS = [
  { value: 'signup_gift', label: '注册赠送' },
  { value: 'campaign', label: '活动赠送' },
  { value: 'manual', label: '人工赠送' },
  { value: 'batch', label: '批量赠送' },
  { value: 'promotion', label: '推广链接奖励' },
  { value: 'referral', label: '邀请奖励' },
  { value: 'purchase', label: '购买续费' },
];

const SOURCE_COLOR: Record<string, string> = {
  signup_gift: 'cyan',
  campaign: 'gold',
  manual: 'blue',
  batch: 'geekblue',
  promotion: 'purple',
  referral: 'magenta',
  purchase: 'green',
};

/**
 * 发放记录：会员时长的流水账（membership_grant），一条发放一行。
 * 它是留痕不是账本——想知道某人现在的档位，看「订阅用户」那一栏，不要从这里反推。
 * GET /admin/membership/grants，查询参数 camelCase（source / keyword / campaignKey / page / pageSize）。
 */
export function GrantsTab() {
  const [source, setSource] = useState<string | undefined>();
  const [keyword, setKeyword] = useState('');
  const [committed, setCommitted] = useState<{ source?: string; keyword?: string }>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['grants', committed, page, pageSize],
    queryFn: () => get<GrantListResponse>('/admin/membership/grants', { ...committed, page, pageSize }),
    placeholderData: (prev) => prev,
  });

  const doSearch = () => {
    setPage(1);
    setCommitted({
      ...(source ? { source } : {}),
      ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
    });
  };

  const resetSearch = () => {
    setSource(undefined);
    setKeyword('');
    setPage(1);
    setCommitted({});
  };

  return (
    <Card
      title="发放记录"
      extra={
        <Space wrap>
          <Select
            allowClear
            placeholder="按来源筛选"
            value={source}
            onChange={setSource}
            options={SOURCE_OPTIONS}
            style={{ width: 160 }}
          />
          <Input
            allowClear
            placeholder="搜索用户姓名 / 说明"
            prefix={<SearchOutlined style={{ color: '#b6bcc2' }} />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={doSearch}
            style={{ width: 200 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={doSearch}>
            查询
          </Button>
          <Button icon={<ReloadOutlined />} onClick={resetSearch}>
            重置
          </Button>
        </Space>
      }
    >
      <Table<GrantRow>
        rowKey="id"
        size="middle"
        loading={isLoading || isFetching}
        dataSource={data?.items ?? []}
        scroll={{ x: 1080 }}
        pagination={{
          current: data?.page ?? page,
          pageSize: data?.pageSize ?? pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条发放记录`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        columns={[
          {
            title: '时间',
            dataIndex: 'createdAt',
            width: 160,
            render: (v: string) => <span style={{ fontSize: 12 }}>{dayjs(v).format('YYYY-MM-DD HH:mm')}</span>,
          },
          {
            title: '用户',
            key: 'party',
            width: 160,
            render: (_, r) => (
              <Typography.Text strong>{r.partyName || '（未填姓名）'}</Typography.Text>
            ),
          },
          {
            title: '来源',
            key: 'source',
            width: 150,
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Tag color={SOURCE_COLOR[r.source] ?? 'default'} style={{ marginInlineEnd: 0 }}>
                  {r.sourceLabel}
                </Tag>
                {r.campaignKey && (
                  <Typography.Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                    {r.campaignKey}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: '时长',
            dataIndex: 'valueText',
            width: 90,
            render: (v: string) => <Typography.Text strong>{v}</Typography.Text>,
          },
          {
            title: '到期时间变化',
            key: 'expire',
            width: 250,
            render: (_, r) => (
              <Typography.Text style={{ fontSize: 12 }}>
                {r.expireBefore ? dayjs(r.expireBefore).format('YYYY-MM-DD HH:mm') : '无会员'}
                {' → '}
                {dayjs(r.expireAfter).format('YYYY-MM-DD HH:mm')}
              </Typography.Text>
            ),
          },
          {
            title: '说明',
            dataIndex: 'reason',
            width: 200,
            render: (v: string | null) => v || <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: '操作人',
            dataIndex: 'operatorName',
            width: 120,
            render: (v: string | null) => v || <Typography.Text type="secondary">系统自动</Typography.Text>,
          },
        ]}
      />
    </Card>
  );
}
